import { PartImageIdentification, PartImageItem } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useCreateBatchFromPartImageItems } from '@/hooks/api/part-finder-image-search/useCreateBatchFromPartImageItems';
import {
  useCropPartImageItem,
  useMergePartImageItems,
  useSplitPartImageItem,
  useUnmergePartImageItem,
} from '@/hooks/api/part-finder-image-search/useEditPartImageItems';
import { getApiError } from '@/types/apiError';
import { pluralize } from '@/utils/format';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import EditIcon from '@mui/icons-material/Edit';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import { Box, Button, Chip, CircularProgress, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PartImageSegmentsByPhoto } from '@/hooks/api/part-finder-image-search/usePartImageSegments';
import { PartImageItemClip, usePartImageItemClips } from './partImageItemClips';
import { firstRegionBox, snapPartImageItems } from './snapPartImageItems';
import { MeasureItemView, MeasuringItem } from './MeasureItemView';
import { EditCropView, EditingCrop } from './EditCropView';
import { editCropPhotos } from './editCrop';
import {
  canMeasure,
  CollapsibleItemList,
  IncludeCheckbox,
  ItemCard,
  ItemHeader,
  ItemIconAction,
  NO_PICK,
  PartPicker,
  photoList,
  topPick,
} from './PartImageItemControls';
import { readTextLabel } from './partImageReadText';
import { WholePhotoRow } from './WholePhotoRow';

const ITEM_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const itemKey = (item: PartImageItem): string => `${item.photoNumbers.join('-')}:${item.description}`;

export const itemsFoundLabel = (identification: PartImageIdentification): string =>
  `Found ${pluralize(identification.items.length, 'item', 'items')} in ${pluralize(identification.imageCount, 'photo', 'photos')}`;

export const itemLabel = (position: number): string => `Item ${ITEM_LETTERS[position] ?? position + 1}`;

const createBatchLabel = (included: number, total: number): string =>
  `Create batch (${included} of ${pluralize(total, 'item', 'items')})`;

const CLIP_HEIGHT = 88;
const CLIP_MAX_WIDTH = 132;

const ClipFrame = styled('img')(({ theme }) => ({
  display: 'block',
  height: CLIP_HEIGHT,
  maxWidth: CLIP_MAX_WIDTH,
  objectFit: 'contain',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const EditCropAction = styled(Box)({
  position: 'absolute',
  top: 4,
  right: 4,
  opacity: 0,
  transition: 'opacity 120ms',
  '& .MuiIconButton-root': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
  '& .MuiIconButton-root:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
});

interface ItemClipsProperties {
  clips: PartImageItemClip[];
  label: string;
  busy: boolean;
  onEditCrop?: () => void;
}

const ClipWithEdit = styled(Box)({ position: 'relative', display: 'inline-flex' });

const ItemClips = ({ clips, label, busy, onEditCrop }: ItemClipsProperties) => (
  <Stack
    gap='6px'
    alignItems='center'
    sx={{ flexShrink: 0, width: CLIP_MAX_WIDTH, '&:hover .edit-crop, &:focus-within .edit-crop': { opacity: 1 } }}
    data-testid='part-image-item-clips'
  >
    {clips.map((clip, index) => (
      <ClipWithEdit key={clip.url}>
        <Tooltip
          placement='right'
          title={
            <Box
              component='img'
              src={clip.url}
              alt={`${label}, photo ${clip.photoNumber}, clip ${index + 1}`}
              sx={{ display: 'block', maxWidth: 360, maxHeight: 360 }}
            />
          }
        >
          <ClipFrame src={clip.outlineUrl} alt={`${label} outlined on photo ${clip.photoNumber}`} />
        </Tooltip>
        {onEditCrop && index === 0 && (
          <EditCropAction className='edit-crop'>
            <ItemIconAction
              label='Edit crop'
              icon={<EditIcon fontSize='small' />}
              testId='part-image-item-edit-crop'
              disabled={busy}
              onClick={onEditCrop}
            />
          </EditCropAction>
        )}
      </ClipWithEdit>
    ))}
  </Stack>
);

interface ShownItem {
  item: PartImageItem;
  index: number;
  label: string;
}

interface MergeMenuProperties {
  index: number;
  shown: ShownItem[];
  disabled: boolean;
  onMerge: (otherIndex: number) => void;
}

const MergeMenu = ({ index, shown, disabled, onMerge }: MergeMenuProperties) => {
  const [anchor, setAnchor] = useState<HTMLElement>();
  const choose = (otherIndex: number) => {
    setAnchor(undefined);
    onMerge(otherIndex);
  };
  return (
    <>
      <ItemIconAction
        label='Merge with another item'
        icon={<CallMergeIcon fontSize='small' />}
        testId='part-image-item-merge'
        disabled={disabled}
        onClick={event => setAnchor(event.currentTarget)}
      />
      <Menu anchorEl={anchor} open={anchor !== undefined} onClose={() => setAnchor(undefined)}>
        {shown
          .filter(other => other.index !== index)
          .map(other => (
            <MenuItem key={itemKey(other.item)} onClick={() => choose(other.index)} sx={{ maxWidth: 480 }}>
              <Typography variant='body2' noWrap>
                <strong>{other.label}</strong> {other.item.description}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

interface ItemPartProperties {
  item: PartImageItem;
  pick: string;
  busy: boolean;
  onPick: (niin: string) => void;
}

const ItemPart = ({ item, pick, busy, onPick }: ItemPartProperties) => {
  if (item.readingCrop) {
    return (
      <Stack direction='row' gap='8px' alignItems='center' sx={{ flex: 1 }} data-testid='part-image-item-reading-crop'>
        <CircularProgress size={14} />
        <Typography variant='body2' color='text.secondary'>
          Reading the new crop…
        </Typography>
      </Stack>
    );
  }
  if (item.candidates.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }} data-testid='part-image-item-no-match'>
        No FLIS match yet. Measure it to find its part.
      </Typography>
    );
  }
  return <PartPicker candidates={item.candidates} pick={pick} disabled={busy} onPick={onPick} />;
};

interface ItemRowActionsProperties {
  item: PartImageItem;
  index: number;
  shown: ShownItem[];
  busy: boolean;
  onMerge: (otherIndex: number) => void;
  onSplit: () => void;
  onUnmerge: () => void;
  onMeasure: () => void;
}

const ItemRowActions = ({
  item,
  index,
  shown,
  busy,
  onMerge,
  onSplit,
  onUnmerge,
  onMeasure,
}: ItemRowActionsProperties) => (
  <>
    {shown.length > 1 && <MergeMenu index={index} shown={shown} disabled={busy} onMerge={onMerge} />}
    {item.merged && (
      <ItemIconAction
        label='Unmerge'
        icon={<CallSplitIcon fontSize='small' />}
        testId='part-image-item-unmerge'
        disabled={busy}
        onClick={onUnmerge}
      />
    )}
    {!item.merged && item.photoNumbers.length > 1 && (
      <ItemIconAction
        label='Split into one item per photo'
        icon={<ViewWeekOutlinedIcon fontSize='small' />}
        testId='part-image-item-split'
        disabled={busy}
        onClick={onSplit}
      />
    )}
    {canMeasure(item) && (
      <ItemIconAction
        label='Measure'
        icon={<StraightenIcon fontSize='small' />}
        testId='part-image-item-measure'
        disabled={busy}
        onClick={onMeasure}
      />
    )}
  </>
);

interface ItemRowProperties {
  shownItem: ShownItem;
  shown: ShownItem[];
  clips: PartImageItemClip[];
  pick: string;
  included: boolean;
  busy: boolean;
  onPick: (niin: string) => void;
  onToggle: () => void;
  onMerge: (otherIndex: number) => void;
  onSplit: () => void;
  onUnmerge: () => void;
  onMeasure: () => void;
  onEditCrop?: () => void;
}

const ItemRow = ({
  shownItem: { item, index, label },
  shown,
  clips,
  pick,
  included,
  busy,
  onPick,
  onToggle,
  onMerge,
  onSplit,
  onUnmerge,
  onMeasure,
  onEditCrop,
}: ItemRowProperties) => (
  <ItemCard data-testid='part-image-item'>
    <IncludeCheckbox
      label={`Include ${label} in batch`}
      included={included}
      picked={pick !== NO_PICK}
      busy={busy}
      onToggle={onToggle}
    />
    {clips.length > 0 && <ItemClips clips={clips} label={label} busy={busy} onEditCrop={onEditCrop} />}
    <Stack gap='8px' sx={{ flex: 1, minWidth: 0 }}>
      <ItemHeader
        title={label}
        subtitle={photoList(item.photoNumbers)}
        description={item.description}
        actions={
          <ItemRowActions
            item={item}
            index={index}
            shown={shown}
            busy={busy}
            onMerge={onMerge}
            onSplit={onSplit}
            onUnmerge={onUnmerge}
            onMeasure={onMeasure}
          />
        }
      />
      {item.cropFailed && (
        <Typography variant='caption' color='error' data-testid='part-image-item-crop-failed'>
          Could not read the new crop. Edit the crop to try again.
        </Typography>
      )}
      {item.readText.length > 0 && (
        <Stack direction='row' gap='6px' flexWrap='wrap'>
          {item.readText.map(text => (
            <Chip key={`${text.kind}:${text.text}`} size='small' label={readTextLabel(text)} />
          ))}
        </Stack>
      )}
      <ItemPart item={item} pick={pick} busy={busy} onPick={onPick} />
    </Stack>
  </ItemCard>
);

const replacedAt = <T,>(values: T[], index: number, value: T): T[] =>
  values.map((existing, position) => (position === index ? value : existing));

const isFittedByUser = (item: PartImageItem): boolean => item.regions.some(region => region.fitted);

const isShown = (item: PartImageItem): boolean => item.candidates.length > 0 || isFittedByUser(item);

const shownItems = (items: PartImageItem[]): ShownItem[] =>
  items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isShown(item))
    .map((shownItem, position) => ({ ...shownItem, label: itemLabel(position) }));

const isCandidate = (item: PartImageItem | undefined, niin: string): boolean =>
  item?.candidates.some(candidate => candidate.niin === niin) ?? false;

const useWholePhotoSelection = (items: PartImageItem[], wholePhoto?: PartImageItem) => {
  const [pick, setPick] = useState(() => (wholePhoto ? topPick(wholePhoto) : NO_PICK));
  const [included, setIncluded] = useState(
    () => wholePhoto?.suggestedNiin !== undefined && !items.some(item => topPick(item) === wholePhoto.suggestedNiin)
  );
  const choose = (niin: string) => {
    setPick(niin);
    setIncluded(true);
  };
  const toggle = () => setIncluded(current => !current);
  const niin = included && isCandidate(wholePhoto, pick) ? pick : undefined;
  return { pick, included, choose, toggle, niin };
};

const candidatesKey = (item: PartImageItem): string => item.candidates.map(candidate => candidate.niin).join(',');

const useItemSelections = (items: PartImageItem[]) => {
  const [picks, setPicks] = useState(() => items.map(item => topPick(item)));
  const [included, setIncluded] = useState(() => items.map(item => item.suggestedNiin !== undefined));
  const [keys, setKeys] = useState(() => items.map(item => candidatesKey(item)));
  const currentKeys = items.map(item => candidatesKey(item));
  if (currentKeys.join('|') !== keys.join('|')) {
    const isReread = (index: number) => currentKeys[index] !== keys[index];
    setKeys(currentKeys);
    setPicks(current => items.map((item, index) => (isReread(index) ? topPick(item) : (current[index] ?? NO_PICK))));
    setIncluded(current =>
      items.map((item, index) => (isReread(index) ? item.suggestedNiin !== undefined : (current[index] ?? false)))
    );
  }
  return { picks, setPicks, included, setIncluded };
};

export interface MultipleItemsResultProperties {
  identification: PartImageIdentification;
  images: File[];
  segments?: PartImageSegmentsByPhoto;
  isSegmenting?: boolean;
  canEditCrop?: boolean;
  onSubview?: (subview?: ResultSubview) => void;
  onBatchCreated: (batchId: string) => void;
}

export interface ResultSubview {
  title: string;
  onBack: () => void;
}

export const MultipleItemsResult = ({
  identification,
  images,
  segments,
  isSegmenting = false,
  canEditCrop = false,
  onSubview,
  onBatchCreated,
}: MultipleItemsResultProperties) => {
  const { items, itemsOverLimit, wholePhoto } = identification;
  const shown = shownItems(items);
  const unmatched = items.flatMap((item, index) => (isShown(item) ? [] : [{ item, index }]));
  const snappedItems = useMemo(() => snapPartImageItems(items, segments), [items, segments]);
  const clips = usePartImageItemClips(images, snappedItems);
  const { picks, setPicks, included, setIncluded } = useItemSelections(items);
  const whole = useWholePhotoSelection(items, wholePhoto);
  const [measuring, setMeasuring] = useState<MeasuringItem>();
  const [editingCrop, setEditingCrop] = useState<EditingCrop>();
  const closeSubview = useCallback(() => {
    setEditingCrop(undefined);
    setMeasuring(undefined);
  }, []);
  const subviewTitle = editingCrop ? `Edit crop of ${editingCrop.label}` : measuring && `Measure ${measuring.label}`;
  const subview = useMemo(
    () => (subviewTitle ? { title: subviewTitle, onBack: closeSubview } : undefined),
    [subviewTitle, closeSubview]
  );
  useEffect(() => onSubview?.(subview), [subview, onSubview]);
  useEffect(() => () => onSubview?.(), [onSubview]);
  const merge = useMergePartImageItems(identification.id);
  const split = useSplitPartImageItem(identification.id);
  const unmerge = useUnmergePartImageItem(identification.id);
  const crop = useCropPartImageItem(identification.id);
  const createBatch = useCreateBatchFromPartImageItems(identification.id);
  const isReadingCrop = items.some(item => item.readingCrop);
  const busy =
    isReadingCrop || merge.isPending || split.isPending || unmerge.isPending || crop.isPending || createBatch.isPending;
  const photosToCrop = useMemo(
    () => snappedItems.map(snapped => editCropPhotos(snapped, images)),
    [snappedItems, images]
  );
  const editCropAction = (editing: EditingCrop) =>
    canEditCrop && photosToCrop[editing.itemIndex]?.length ? () => setEditingCrop(editing) : undefined;
  const selections = shown.flatMap(({ index }) => {
    const niin = picks[index] ?? NO_PICK;
    const box = firstRegionBox(snappedItems[index]);
    return included[index] && isCandidate(items[index], niin) ? [{ itemIndex: index, niin, box }] : [];
  });
  const selectedCount = selections.length + (whole.niin ? 1 : 0);
  const choosableCount = shown.length + (wholePhoto?.candidates.length ? 1 : 0);

  const toggle = (index: number) => setIncluded(current => replacedAt(current, index, !current[index]));
  const pickFor = (index: number) => (niin: string) => {
    setPicks(current => replacedAt(current, index, niin));
    setIncluded(current => replacedAt(current, index, true));
  };
  const measuredRow = (target: MeasuringItem['target']) => {
    if (target.wholePhoto) return { candidates: wholePhoto?.candidates ?? [], pick: whole.pick, onPick: whole.choose };
    const index = target.itemIndex ?? -1;
    return { candidates: items[index]?.candidates ?? [], pick: picks[index] ?? NO_PICK, onPick: pickFor(index) };
  };
  const handleCreateBatch = () =>
    createBatch.mutate({ selections, wholePhotoNiin: whole.niin }, { onSuccess: onBatchCreated });

  const apiError = getApiError(
    merge.error ?? split.error ?? unmerge.error ?? crop.error ?? createBatch.error ?? undefined,
    'Could not update items'
  );

  if (measuring) {
    return (
      <MeasureItemView identificationId={identification.id} measuring={measuring} {...measuredRow(measuring.target)} />
    );
  }

  if (editingCrop) {
    return (
      <EditCropView
        editing={editingCrop}
        photos={photosToCrop[editingCrop.itemIndex] ?? []}
        busy={busy}
        error={apiError}
        onBack={() => setEditingCrop(undefined)}
        onSave={(photoNumber, box) =>
          crop.mutate(
            { itemIndex: editingCrop.itemIndex, photoNumber, box },
            { onSuccess: () => setEditingCrop(undefined) }
          )
        }
      />
    );
  }

  return (
    <Stack gap='12px' sx={{ flex: { md: 1 }, minHeight: 0 }} data-testid='part-image-items'>
      {isSegmenting && (
        <Stack direction='row' gap='6px' alignItems='center' data-testid='part-image-items-tightening'>
          <CircularProgress size={12} />
          <Typography variant='caption' color='text.secondary'>
            Tightening item outlines…
          </Typography>
        </Stack>
      )}
      {itemsOverLimit.length > 0 && (
        <CollapsibleItemList
          severity='warning'
          testId='part-image-items-over-limit'
          items={itemsOverLimit}
          message={`${pluralize(itemsOverLimit.length, 'more item was', 'more items were')} found but not searched. Search them in another photo search.`}
        />
      )}
      {unmatched.length > 0 && (
        <CollapsibleItemList
          severity='info'
          testId='part-image-items-unmatched'
          items={unmatched.map(({ item }) => item)}
          message={`${pluralize(unmatched.length, 'more item', 'more items')} had no FLIS match. Measure one to find its part.`}
          actionDisabled={busy}
          onMeasure={position =>
            setMeasuring({
              target: { itemIndex: unmatched[position].index },
              label: 'this item',
              description: unmatched[position].item.description,
            })
          }
          onEditCrop={
            canEditCrop
              ? position =>
                  setEditingCrop({
                    itemIndex: unmatched[position].index,
                    label: 'this item',
                    description: unmatched[position].item.description,
                  })
              : undefined
          }
        />
      )}
      <ApiErrorAlert error={apiError} />
      <Stack gap='8px' sx={{ overflowY: { md: 'auto' }, flex: { md: 1 }, minHeight: 0, pr: '4px' }}>
        {wholePhoto && (
          <WholePhotoRow
            item={wholePhoto}
            photo={images[0]}
            pick={whole.pick}
            included={whole.included}
            busy={busy}
            onPick={whole.choose}
            onToggle={whole.toggle}
            onMeasure={() =>
              setMeasuring({
                target: { wholePhoto: true },
                label: 'the whole photo',
                description: wholePhoto.description,
              })
            }
          />
        )}
        {shown.map(shownItem => (
          <ItemRow
            key={`${itemKey(shownItem.item)}-${shownItem.index}`}
            shownItem={shownItem}
            shown={shown}
            clips={clips[shownItem.index] ?? []}
            pick={picks[shownItem.index] ?? NO_PICK}
            included={included[shownItem.index] ?? false}
            busy={busy}
            onPick={pickFor(shownItem.index)}
            onToggle={() => toggle(shownItem.index)}
            onMerge={otherIndex => merge.mutate([shownItem.index, otherIndex])}
            onSplit={() => split.mutate(shownItem.index)}
            onUnmerge={() => unmerge.mutate(shownItem.index)}
            onMeasure={() =>
              setMeasuring({
                target: { itemIndex: shownItem.index },
                label: shownItem.label,
                description: shownItem.item.description,
              })
            }
            onEditCrop={editCropAction({
              itemIndex: shownItem.index,
              label: shownItem.label,
              description: shownItem.item.description,
            })}
          />
        ))}
      </Stack>
      <Stack
        direction='row'
        gap='8px'
        alignItems='center'
        sx={{ pt: '12px', borderTop: 1, borderColor: 'divider' }}
        data-testid='part-image-items-footer'
      >
        <Typography variant='caption' color='text.secondary'>
          Checked rows with a part picked go into the batch, one report each.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant='contained'
          disabled={busy || selectedCount === 0}
          onClick={handleCreateBatch}
          data-testid='part-image-items-create-batch'
        >
          {createBatchLabel(selectedCount, choosableCount)}
        </Button>
      </Stack>
    </Stack>
  );
};
